import {type ClassValue, clsx} from "clsx";
import {twMerge} from "tailwind-merge";
import * as Icon from "../icons";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getRandomEmoji() {
  const emoji =
    "🍳 🍔 🍕 🥪 🍝 🌮 🍣 🥗 🍗 🍤 🧀 🍪 🥞 🍅 🥦 🍯 🍹 🍆 🌽 🍖 🍛 🍜 🍱 🍲 🍥 🍢 🍚 🍞 🍌 🍎 🥑 🍋 🍓 🍇 🍈 🍉 🍊 🍒 🍍 🥕 🥔 🌶️ 🧅 🧄 🥓 🍅 🥯 🥖 🍴 🥄 🍴 🫘 🍔 🥚 🍽️ 🥢 📦 👩‍🍳 👨‍🍳";
  const randomEmoji = emoji.split(" ");
  return randomEmoji[Math.floor(Math.random() * randomEmoji.length)];
}

export const spring = {
  snappy: {type: "spring", stiffness: 2000, damping: 40, mass: 0.01},
  bouncy: {type: "spring", stiffness: 650, damping: 30, mass: 1},
  smooth: {type: "spring", stiffness: 500, damping: 40, mass: 1},
};

export function randomRange(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

export const browser = {
  isChrome: navigator.userAgent.includes("Chrome"),
};

export const hasSellingPlans = (productJson: RootObject) => {
  return productJson?.variants?.some(
    (variant) =>
      variant?.selling_plan_allocations &&
      variant?.selling_plan_allocations?.length > 0
  );
};

function addCustomerParams(params: URLSearchParams) {
  if (typeof CUSTOMER !== "undefined") {
    const {email, defaultAddress} = CUSTOMER;

    params.append("email", email);
    params.append(
      "checkout[shipping_address][first_name]",
      defaultAddress.first_name
    );
    params.append(
      "checkout[shipping_address][last_name]",
      defaultAddress.last_name
    );
    params.append(
      "checkout[shipping_address][address1]",
      defaultAddress.address1
    );
    params.append("checkout[shipping_address][city]", defaultAddress.city);
    params.append(
      "checkout[shipping_address][province]",
      defaultAddress.province
    );
    params.append(
      "checkout[shipping_address][country]",
      defaultAddress.country
    );
    params.append("checkout[shipping_address][zip]", defaultAddress.zip);
  }
}

function buildLineItems(items: Array<{[key: string]: any}>): string {
  const params = new URLSearchParams();

  for (const item of items) {
    for (const key in item) {
      params.append(`items[][${key}]`, item[key]);
    }
  }

  return params.toString();
}

export function goToCheckoutViaCart(
  lineItems: Array<{[key: string]: any}>,
  params: Params
) {
  window.location.href = buildCheckoutViaCartUrl(lineItems, params);
}

export function buildCheckoutViaCartUrl(
  lineItems: Array<{[key: string]: any}>,
  params: Params
): string {
  const {attributes, ...otherParams} = params;
  const checkoutParams = new URLSearchParams(
    otherParams as Record<string, string>
  );

  if (attributes) {
    for (const key in attributes) {
      checkoutParams.append(
        `attributes[${key}]`,
        String(attributes[key as keyof Attributes])
      );
    }
  }

  addCustomerParams(checkoutParams);

  const checkout_url = `/checkout?${checkoutParams.toString()}`;

  const add_to_cart_url = `/cart/add?${buildLineItems(
    lineItems
  )}&return_to=${checkout_url}`;
  return `/cart/clear?return_to=${encodeURIComponent(add_to_cart_url)}`;
}

export function getDecoratedTag(tag: any) {
  switch (tag.handle) {
    case "gluteenivaba":
      return {
        ...tag,
        classNames:
          "bg-amber-100/50 text-amber-500 hover:bg-amber-200/70 hover:text-amber-500 active:bg-amber-400 active:text-white",
        Icon: Icon.WheatOff,
      };
    case "kala":
      return {
        ...tag,
        classNames:
          "bg-blue-50 text-blue-400 hover:bg-blue-100 hover:text-blue-500 active:bg-blue-400 active:text-white",
        Icon: Icon.Fish,
      };
    case "laktoosivaba":
      return {
        ...tag,
        classNames:
          "bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-500 active:bg-slate-400 active:text-white",
        Icon: Icon.MilkOff,
      };
    case "preemium":
      return {
        ...tag,
        classNames:
          "bg-purple-50 text-purple-400 hover:bg-purple-100 hover:text-purple-500 active:bg-purple-400 active:text-white",
      };
    case "liha":
      return {
        ...tag,
        classNames:
          "bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-500 active:bg-red-400 active:text-white",
        Icon: Icon.Beef,
      };
    case "kana":
      return {
        ...tag,
        classNames:
          "bg-orange-50 text-orange-400 hover:bg-orange-100 hover:text-orange-500 active:bg-orange-400 active:text-white",
        Icon: Icon.DrumStick,
      };
    case "taimetoit":
      return {
        ...tag,
        classNames:
          "bg-green-50 text-green-500 hover:bg-green-100 hover:text-green-600 active:bg-green-500 active:text-white",
        Icon: Icon.Plant,
      };
    case "vegan":
      return {
        ...tag,
        classNames:
          "bg-lime-50 text-lime-500 hover:bg-lime-100 hover:text-lime-600 active:bg-lime-500 active:text-white",
        Icon: Icon.Vegan,
      };
    case "magustoit":
      return {
        ...tag,
        classNames:
          "bg-pink-50 text-pink-500 hover:bg-pink-100 hover:text-pink-600 active:bg-pink-500 active:text-white",
        Icon: Icon.Dessert,
      };
    case "peresobralik":
      return {
        ...tag,
        classNames:
          "bg-teal-50 text-teal-500 hover:bg-teal-100 hover:text-teal-600 active:bg-teal-500 active:text-white",
        Icon: Icon.Smile,
      };
    default:
      return {
        ...tag,
        classNames: "bg-zinc-50 text-zinc-400",
        Icon: null,
      };
  }
}
